import React, { useState } from 'react';
import { useMalcolmContext } from '../../contexts/MalcolmContext';

const IntelligenceFeedSidebar = () => {
  const {
    insights,
    savedItems,
    skills,
    betaOpportunities,
    companyIntelligence,
    dismissInsight,
    updateSkillLevel
  } = useMalcolmContext();

  const [activeTab, setActiveTab] = useState('insights');

  const tabs = [
    { id: 'insights', label: 'AI Insights', count: insights.length },
    { id: 'workspace', label: 'Research', count: savedItems.length },
    { id: 'trends', label: 'Trends', count: null },
    { id: 'skills', label: 'Skills', count: skills.length }
  ];

  const renderInsights = () => (
    <div className="space-y-4">
      {insights.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-indigo-600">🤖</span>
          </div>
          <p className="text-stone-600 text-sm">No insights available yet</p>
        </div>
      ) : (
        insights.map((insight) => (
          <div
            key={insight.id}
            className={`
              p-4 rounded-lg border-l-4 relative
              ${insight.insight_type === 'strategic' ? 'bg-indigo-50 border-l-indigo-500' :
                insight.insight_type === 'opportunity' ? 'bg-teal-50 border-l-teal-500' :
                'bg-red-50 border-l-red-500'}
            `}
          >
            <div className="flex items-start justify-between mb-2">
              <div className={`
                px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide
                ${insight.insight_type === 'strategic' ? 'bg-indigo-100 text-indigo-700' :
                  insight.insight_type === 'opportunity' ? 'bg-teal-100 text-teal-700' :
                  'bg-red-100 text-red-700'}
              `}>
                {insight.insight_type}
              </div>
              <button
                onClick={() => dismissInsight(insight.id)}
                className="text-stone-400 hover:text-stone-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <h4 className="font-semibold text-slate-900 mb-2">{insight.title}</h4>
            <p className="text-sm text-stone-700 mb-3 leading-relaxed">{insight.content}</p>
            
            <div className="flex items-center justify-between text-xs text-stone-500">
              <span>{Math.round(insight.confidence_score)}% confidence</span>
              <span>{new Date(insight.generated_at).toLocaleDateString()}</span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderWorkspace = () => (
    <div className="space-y-3">
      {savedItems.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-teal-600">📚</span>
          </div>
          <p className="text-stone-600 text-sm">No saved items yet</p>
        </div>
      ) : (
        <>
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {['all', 'implementation', 'analysis', 'research', 'learning'].map((category) => (
              <button
                key={category}
                className="px-3 py-1 text-xs font-medium rounded-full bg-stone-100 text-stone-700 hover:bg-stone-200 transition-colors capitalize"
              >
                {category}
                {category !== 'all' && (
                  <span className="ml-1 bg-stone-200 text-stone-600 rounded-full px-1">
                    {savedItems.filter(item => item.category === category).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Saved items */}
          {savedItems.map((savedItem) => (
            <div key={savedItem.id} className="bg-stone-50 rounded-lg p-3 border border-stone-200">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-slate-900 text-sm leading-tight line-clamp-2">
                  {savedItem.item?.title}
                </h4>
                <div className={`
                  px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2
                  ${savedItem.category === 'implementation' ? 'bg-azure-100 text-azure-700' :
                    savedItem.category === 'analysis' ? 'bg-violet-100 text-violet-700' :
                    savedItem.category === 'research' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-orange-100 text-orange-700'}
                `}>
                  {savedItem.category}
                </div>
              </div>
              
              {savedItem.notes && (
                <p className="text-xs text-stone-600 mb-2">{savedItem.notes}</p>
              )}
              
              <div className="text-xs text-stone-500">
                Saved {new Date(savedItem.saved_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );

  const renderTrends = () => (
    <div className="space-y-4">
      {/* Trending topics */}
      <div>
        <h4 className="font-semibold text-slate-900 mb-3">Trending This Week</h4>
        <div className="space-y-3">
          {[
            { term: 'Multimodal AI Agents', mentions: 340, companies: 12, trend: 'hot' },
            { term: 'Small Language Models', mentions: 180, companies: 8, trend: 'rising' },
            { term: 'AI Safety Frameworks', mentions: 90, companies: 6, trend: 'emerging' },
            { term: 'Code Generation Tools', mentions: 150, companies: 10, trend: 'rising' }
          ].map((trend, index) => (
            <div key={index} className="flex items-center space-x-3 py-2">
              <div className={`
                w-2 h-2 rounded-full
                ${trend.trend === 'hot' ? 'bg-red-500' :
                  trend.trend === 'rising' ? 'bg-pink-500' :
                  'bg-violet-500'}
              `}></div>
              <div className="flex-1">
                <div className="font-medium text-sm text-slate-900">{trend.term}</div>
                <div className="text-xs text-stone-500">
                  +{trend.mentions}% mentions, {trend.companies} companies
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Company momentum */}
      <div>
        <h4 className="font-semibold text-slate-900 mb-3">Company Intelligence</h4>
        <div className="space-y-3">
          {companyIntelligence.slice(0, 4).map((company) => (
            <div key={company.id} className="bg-stone-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-sm text-slate-900">{company.company_name}</div>
                <div className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${company.momentum === 'high' ? 'bg-red-100 text-red-700' :
                    company.momentum === 'steady' ? 'bg-teal-100 text-teal-700' :
                    'bg-stone-100 text-stone-600'}
                `}>
                  {company.momentum} momentum
                </div>
              </div>
              
              <div className="text-xs text-stone-600 mb-2">
                {company.recent_releases} releases this month • {company.strategy} strategy
              </div>
              
              {company.analysis_summary && (
                <p className="text-xs text-stone-700 italic">{company.analysis_summary}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Beta opportunities */}
      <div>
        <h4 className="font-semibold text-slate-900 mb-3">Beta Opportunities</h4>
        <div className="space-y-3">
          {betaOpportunities.slice(0, 3).map((beta) => (
            <div key={beta.id} className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-lg p-3 border-l-4 border-l-violet-500">
              <div className="font-medium text-sm text-slate-900 mb-1">{beta.title}</div>
              <div className="text-xs text-stone-600 mb-2">{beta.description}</div>
              {beta.deadline && (
                <div className="text-xs text-violet-600 font-medium">
                  Deadline: {new Date(beta.deadline).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSkills = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-900">Skill Development</h4>
        <button className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">
          + Add Skill
        </button>
      </div>
      
      {skills.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-teal-600">📈</span>
          </div>
          <p className="text-stone-600 text-sm">No skills tracked yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {skills.map((skill) => (
            <div key={skill.id} className="bg-stone-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-sm text-slate-900">{skill.skill_name}</div>
                <div className="text-xs text-stone-500">{skill.current_level}%</div>
              </div>
              
              <div className="w-full bg-stone-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${skill.current_level}%` }}
                ></div>
              </div>
              
              {skill.target_level && (
                <div className="text-xs text-stone-600">
                  Target: {skill.target_level}%
                </div>
              )}
              
              {skill.learning_resources && skill.learning_resources.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs text-stone-500 mb-1">Resources:</div>
                  <div className="flex flex-wrap gap-1">
                    {skill.learning_resources.slice(0, 2).map((resource, index) => (
                      <span key={index} className="text-xs bg-stone-200 text-stone-700 px-2 py-1 rounded">
                        {resource}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white border border-stone-200 rounded-lg h-fit sticky top-24">
      {/* Tabs */}
      <div className="border-b border-stone-200">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 px-3 py-3 text-sm font-medium text-center border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
                }
              `}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className={`
                  ml-2 px-2 py-0.5 rounded-full text-xs
                  ${activeTab === tab.id
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-stone-100 text-stone-600'
                  }
                `}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto">
        {activeTab === 'insights' && renderInsights()}
        {activeTab === 'workspace' && renderWorkspace()}
        {activeTab === 'trends' && renderTrends()}
        {activeTab === 'skills' && renderSkills()}
      </div>
    </div>
  );
};

export default IntelligenceFeedSidebar;